import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../auth/AuthProvider";
import { Link, useParams, useNavigate } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
async function apiSend(path, method, body) {
  const opts = { method, credentials: "include", headers: { "Content-Type": "application/json" } };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const r = await fetch(`${BACKEND}${path}`, opts);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw { status: r.status, data: j };
  return j;
}
const apiDelete = (p) => apiSend(p, "DELETE");
const apiPatch = (p, b) => apiSend(p, "PATCH", b);

/**
 * v43+PDF (Mar 2026) — Multichannel Order Intake Office UI.
 *
 * Two-in-one file per keep the codebase tight:
 *   - AdminOrderImportsListPage — list + "Nuovo import" upload wizard
 *   - AdminOrderImportDetailPage — review table + manual match + confirm CTA
 */

export function AdminOrderImportsListPage() {
  const { apiGet } = useAuth();
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    const r = await apiGet("/api/admin/order-imports").catch(() => ({ items: [] }));
    setItems(r.items || []);
    setLoading(false);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div style={{ padding: 24, maxWidth: 1300, margin: "0 auto" }} data-testid="order-imports-list">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Ordini da importare</h1>
        <button
          data-testid="new-import-btn"
          onClick={() => setShowNew(true)}
          style={{ padding: "10px 18px", background: "#0F4C81", color: "#fff", borderRadius: 8, border: 0, cursor: "pointer", fontWeight: 600 }}>
          + NUOVO IMPORT
        </button>
      </div>
      {showNew && <NewImportModal onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); reload(); }} />}
      {loading ? <p>Caricamento…</p> : (
        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 8, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <thead style={{ background: "#f6f8fb" }}>
            <tr>
              {["Data","Customer","File","Tipo","Origine","External #","Status","Ordine creato","Op."].map(h => (
                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 13, color: "#555" }}>{h}</th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} style={{ padding: 24, textAlign: "center", color: "#888" }}>Nessun import ancora.</td></tr>}
            {items.map(it => (
              <tr key={it.id} data-testid={`import-row-${it.id}`} style={{ borderTop: "1px solid #eee" }}>
                <td style={{ padding: "10px 12px", fontSize: 13 }}>{new Date(it.created_at).toLocaleString("it-IT")}</td>
                <td style={{ padding: "10px 12px", fontSize: 13 }}>{it.customer_business_name || "—"} <span style={{ color: "#888" }}>({it.customer_code || "—"})</span></td>
                <td style={{ padding: "10px 12px", fontSize: 13 }}>{it.original_filename}</td>
                <td style={{ padding: "10px 12px", fontSize: 12, textTransform: "uppercase" }}>{(it.original_filename || "").split(".").pop()}</td>
                <td style={{ padding: "10px 12px", fontSize: 11 }} data-testid={`source-badge-${it.id}`}>
                  {it.source_type === "CUSTOMER_UPLOAD"
                    ? <span style={{ background: "#e0f2fe", color: "#075985", padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>Caricato dal Customer</span>
                    : it.source_type === "EMAIL_IMPORT"
                    ? <span style={{ background: "#fce7f3", color: "#9d174d", padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>Ricevuto via email</span>
                    : <span style={{ background: "#f3f4f6", color: "#374151", padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>Importazione Office</span>}
                </td>
                <td style={{ padding: "10px 12px", fontSize: 13 }}>{it.external_order_number || "—"}</td>
                <td style={{ padding: "10px 12px" }}><StatusBadge status={it.status} /></td>
                <td style={{ padding: "10px 12px", fontSize: 13 }}>{it.created_order_id ? "SÌ" : "—"}</td>
                <td style={{ padding: "10px 12px", fontSize: 13 }}>{it.created_by_user_id ? "✓" : ""}</td>
                <td style={{ padding: "10px 12px" }}>
                  <Link to={`/admin/order-imports/${it.id}`} data-testid={`open-import-${it.id}`} style={{ color: "#0F4C81", fontWeight: 600 }}>APRI</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    UPLOADED: { bg: "#e5e7eb", fg: "#374151" },
    REVIEW_REQUIRED: { bg: "#fef3c7", fg: "#92400e" },
    READY: { bg: "#d1fae5", fg: "#065f46" },
    CONFIRMED: { bg: "#dbeafe", fg: "#1e40af" },
    REJECTED: { bg: "#fee2e2", fg: "#991b1b" },
  };
  const s = map[status] || { bg: "#e5e7eb", fg: "#374151" };
  return <span data-testid={`status-badge-${status}`} style={{ background: s.bg, color: s.fg, padding: "2px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600 }}>{status}</span>;
}

function NewImportModal({ onClose, onCreated }) {
  const { apiGet } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [externalOrderNumber, setExternalOrderNumber] = useState("");
  const [requestedDate, setRequestedDate] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    apiGet("/api/customers?limit=500").then(r => setCustomers(r.items || r || [])).catch(() => setCustomers([]));
  }, []);

  const submit = async () => {
    if (!customerId || !file) { setErr("Cliente e file sono obbligatori"); return; }
    setErr(""); setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("customer_id", customerId);
      fd.append("file", file);
      if (externalOrderNumber) fd.append("external_order_number", externalOrderNumber);
      if (requestedDate) fd.append("requested_delivery_date", requestedDate);
      const backend = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backend}/api/admin/order-imports`, { method: "POST", credentials: "include", body: fd });
      const j = await resp.json();
      if (!resp.ok) {
        if (j?.detail?.code === "DUPLICATE_IMPORT") {
          setErr(`Questo file risulta già importato per il Customer selezionato (import #${j.detail.existing_import_id}).`);
        } else setErr(j?.detail?.message || j?.detail || "Errore upload");
        setSubmitting(false); return;
      }
      onCreated(j.id);
      navigate(`/admin/order-imports/${j.id}`);
    } catch (e) { setErr(String(e)); setSubmitting(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} data-testid="new-import-modal">
      <div style={{ background: "#fff", padding: 28, borderRadius: 10, width: 520, maxWidth: "90vw" }}>
        <h2 style={{ marginTop: 0 }}>Nuovo import ordine</h2>
        <div style={{ display: "grid", gap: 12 }}>
          <label>Cliente
            <select data-testid="customer-select" value={customerId} onChange={e => setCustomerId(e.target.value)} style={{ width: "100%", padding: 8, marginTop: 4 }}>
              <option value="">— Seleziona —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.code} · {c.business_name}</option>)}
            </select>
          </label>
          <label>External order number (opzionale)
            <input data-testid="external-order-number" value={externalOrderNumber} onChange={e => setExternalOrderNumber(e.target.value)} style={{ width: "100%", padding: 8, marginTop: 4 }} />
          </label>
          <label>Data consegna richiesta (opzionale)
            <input type="date" data-testid="requested-delivery-date" value={requestedDate} onChange={e => setRequestedDate(e.target.value)} style={{ width: "100%", padding: 8, marginTop: 4 }} />
          </label>
          <label>File ordine (.csv .xls .xlsx .pdf)
            <input type="file" data-testid="file-input" accept=".csv,.xls,.xlsx,.pdf" onChange={e => setFile(e.target.files[0])} style={{ width: "100%", padding: 8, marginTop: 4 }} />
          </label>
          {err && <div style={{ color: "#991b1b", background: "#fee2e2", padding: 8, borderRadius: 4, fontSize: 13 }} data-testid="upload-error">{err}</div>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
            <button onClick={onClose} disabled={submitting} style={{ padding: "8px 16px" }}>Annulla</button>
            <button data-testid="analyze-btn" onClick={submit} disabled={submitting || !customerId || !file} style={{ padding: "8px 16px", background: "#0F4C81", color: "#fff", border: 0, borderRadius: 6, fontWeight: 600 }}>
              {submitting ? "Analisi in corso…" : "ANALIZZA ORDINE"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminOrderImportDetailPage() {
  const { apiGet, apiPost } = useAuth();
  const { id } = useParams();
  const [imp, setImp] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // v43+PDF UX (Mar 2026) — Data richiesta di consegna gestita SOLO in FE per
  // preservare l'edit manuale dell'Office. Se il PDF estrae una data valida
  // deterministica, la usiamo come suggerimento iniziale. Poi refresh/preview
  // NON la sovrascrivono più: `userEditedDate` diventa la sorgente autoritativa.
  const [requestedDate, setRequestedDate] = useState("");
  const [userEditedDate, setUserEditedDate] = useState(false);
  const [dateFromPdf, setDateFromPdf] = useState(false);
  const navigate = useNavigate();

  // Deterministic parse. Accepts 'YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY',
  // 'DD.MM.YYYY'. Ambiguous / partial-year input → blank. NO guessing.
  const _normalizeDate = (raw) => {
    if (!raw || typeof raw !== "string") return "";
    const s = raw.trim();
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
    if (m) {
      const dd = m[1].padStart(2, "0");
      const mm = m[2].padStart(2, "0");
      const yyyy = m[3];
      const d = parseInt(dd, 10), mo = parseInt(mm, 10);
      if (d < 1 || d > 31 || mo < 1 || mo > 12) return "";
      return `${yyyy}-${mm}-${dd}`;
    }
    return "";
  };

  const reload = useCallback(async () => {
    try {
      const d = await apiGet(`/api/admin/order-imports/${id}`);
      setImp(d);
      // Prefill "Data richiesta" ONLY on first load or when Office hasn't edited it.
      // Manual value ALWAYS wins over PDF extraction / refresh cycles.
      if (!userEditedDate) {
        if (d.requested_delivery_date) {
          setRequestedDate(d.requested_delivery_date);
          setDateFromPdf(false);
        } else {
          const raw = d.parser_meta?.extracted_requested_delivery_date_raw;
          // Prefill only for TEXT_PDF / PARTIAL_TEXT_PDF; IMAGE_ONLY_PDF → NO extraction.
          const cls = d.parser_meta?.pdf_classification;
          const safeToPrefill = !cls || cls === "TEXT_PDF" || cls === "PARTIAL_TEXT_PDF";
          const normalized = safeToPrefill ? _normalizeDate(raw) : "";
          if (normalized) {
            setRequestedDate(normalized);
            setDateFromPdf(true);
          } else {
            setDateFromPdf(false);
          }
        }
      }
    }
    catch (e) { setErr(String(e)); }
  }, [id, apiGet, userEditedDate]);
  useEffect(() => { reload(); }, [reload]);

  if (!imp && !err) return <div style={{ padding: 40 }}>Caricamento…</div>;
  if (err) return <div style={{ padding: 40, color: "#991b1b" }}>{err}</div>;

  const unresolved = (imp.lines || []).filter(l => ["NOT_FOUND", "AMBIGUOUS", "INVALID"].includes(l.match_status));
  const canConfirm = imp.status === "READY" && unresolved.length === 0;
  const isFinal = imp.status === "CONFIRMED" || imp.status === "REJECTED";

  const doConfirm = async () => {
    // v43.35.21 — product matching MUST NOT infer a delivery_mode from the
    // absence of delivery_point_id. CUSTOMER_UPLOAD drafts arrive without
    // delivery metadata; silently sending delivery_mode="PICKUP" caused
    // `create_order` to raise `pickup_location_required` the moment the
    // operator clicked CONFERMA. Decoupled: product verification completes
    // standalone; commercial confirmation stays blocked here with a visible
    // actionable message until the operator sets a real delivery context.
    if (!imp.delivery_point_id) {
      setErr("Imposta un punto di consegna (DELIVERY) o la sede di ritiro (PICKUP) sull'ordine prima della conferma. L'associazione dei prodotti è completa; la modalità di consegna va scelta esplicitamente.");
      return;
    }
    if (!window.confirm(`Verrà creato un ordine reale nel flusso GE.\n\nCustomer: ${imp.customer_business_name}\nRighe: ${imp.lines.length}\nExternal #: ${imp.external_order_number || "—"}\nData richiesta: ${requestedDate || "—"}\n\nConfermare?`)) return;
    setBusy(true); setErr("");
    try {
      const r = await apiPost(`/api/admin/order-imports/${id}/confirm`, {
        delivery_mode: "DELIVERY",
        delivery_point_id: imp.delivery_point_id,
        requested_delivery_date: requestedDate || null,
      });
      alert(`Ordine creato: ${r.order_number}`);
      navigate(`/admin/order-imports/${id}`);
      reload();
    } catch (e) {
      const d = e?.data?.detail;
      if (d?.code === "REQUESTED_DELIVERY_DATE_BEFORE_ESTIMATED") {
        setErr(`La data richiesta è precedente alla data prevista di consegna (${d.estimated_delivery_date}).`);
      } else if (d?.code === "UNRESOLVED_LINES") {
        setErr(`Ci sono ${d.count} righe non risolte. Risolvi tutte le righe prima di creare l'ordine.`);
      } else setErr(d?.message || d || String(e));
    } finally { setBusy(false); }
  };

  const rejectImport = async () => {
    const notes = window.prompt("Motivo del rifiuto?", "");
    if (notes === null) return;
    await apiPost(`/api/admin/order-imports/${id}/reject`, { notes });
    reload();
  };

  const preview = imp.pricing_preview || {};

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }} data-testid="import-detail">
      <Link to="/admin/order-imports" style={{ color: "#0F4C81" }}>← Torna alla lista</Link>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, margin: 0 }}>{imp.original_filename}</h1>
          <p style={{ margin: "4px 0", color: "#555" }}>{imp.customer_business_name} · <span style={{ color: "#888" }}>{imp.customer_code}</span></p>
          <p style={{ margin: "4px 0", fontSize: 13, color: "#666" }}>
            <StatusBadge status={imp.status} /> · External #: {imp.external_order_number || "—"} · SHA256: <code>{imp.file_sha256?.slice(0, 12)}…</code>
            {imp.parser_meta?.pdf_classification && <> · <b>PDF: {imp.parser_meta.pdf_classification}</b></>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {imp.storage_key && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/order-imports/${imp.id}/file`}
               target="_blank" rel="noreferrer"
               data-testid="download-original"
               style={{ padding: "8px 14px", border: "1px solid #0F4C81", color: "#0F4C81", borderRadius: 6, textDecoration: "none" }}>
              ORIGINALE
            </a>
          )}
          {!isFinal && (
            <button data-testid="reject-btn" onClick={rejectImport} style={{ padding: "8px 14px", border: "1px solid #991b1b", background: "#fff", color: "#991b1b", borderRadius: 6 }}>Rifiuta</button>
          )}
          {!isFinal && (
            <button
              data-testid="confirm-btn"
              onClick={doConfirm}
              disabled={!canConfirm || busy}
              style={{ padding: "10px 20px", background: canConfirm ? "#065f46" : "#9ca3af", color: "#fff", border: 0, borderRadius: 6, fontWeight: 700, cursor: canConfirm ? "pointer" : "not-allowed" }}>
              {busy ? "Creazione…" : "CONFERMA E CREA ORDINE"}
            </button>
          )}
        </div>
      </div>

      {err && <div style={{ marginTop: 12, color: "#991b1b", background: "#fee2e2", padding: 10, borderRadius: 4 }} data-testid="detail-error">{err}</div>}

      {!isFinal && (
        <div style={{ marginTop: 16, display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555", fontWeight: 600 }}>Data richiesta di consegna</span>
            <input
              type="date"
              data-testid="import-requested-date"
              value={requestedDate}
              onChange={(e) => { setRequestedDate(e.target.value); setUserEditedDate(true); setDateFromPdf(false); }}
              style={{ padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 4 }} />
            {dateFromPdf && (
              <span style={{ fontSize: 11, color: "#0F4C81", fontStyle: "italic" }} data-testid="date-from-pdf-helper">
                Data rilevata dal documento
              </span>
            )}
            {requestedDate && userEditedDate && (
              <button
                type="button"
                data-testid="import-clear-date"
                onClick={() => { setRequestedDate(""); setUserEditedDate(true); setDateFromPdf(false); }}
                style={{ fontSize: 11, color: "#991b1b", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}>
                Cancella data
              </button>
            )}
          </label>
        </div>
      )}
      {!canConfirm && !isFinal && unresolved.length > 0 && (
        <div style={{ marginTop: 12, background: "#fef3c7", padding: 10, borderRadius: 4, color: "#92400e" }}>
          Risolvi tutte le righe prima di creare l'ordine. Non risolte: <b>{unresolved.length}</b>.
        </div>
      )}
      {imp.parser_meta?.pdf_classification === "IMAGE_ONLY_PDF" && (
        <div style={{ marginTop: 12, background: "#fef3c7", padding: 12, borderRadius: 4, color: "#92400e" }}>
          Il PDF non contiene testo leggibile automaticamente. Inserisci o verifica manualmente le righe dell'ordine.
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 24, background: "#fff", fontSize: 13 }}>
        <thead style={{ background: "#f6f8fb" }}>
          <tr>
            {["Row","Cust. SKU","Descrizione","Qty","UOM","GE SKU","GE Product","Cartons","Kg","Cust. €/collo","GE €/kg","GE €/collo","Δ €/collo","Match","Validation",""].map(h => (
              <th key={h} style={{ padding: "8px 6px", textAlign: "left", fontSize: 12, color: "#555" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(imp.lines || []).map(l => {
            const previewLine = (preview.lines || []).find(p => p.import_line_id === l.id);
            return (
              <ImportLineRow key={l.id} l={l} previewLine={previewLine} importId={id}
                              onReload={reload} disabled={isFinal} />
            );
          })}
        </tbody>
      </table>

      {preview.total !== null && preview.total !== undefined && (
        <div style={{ marginTop: 16, textAlign: "right" }}>
          <b>Totale GE: € {Number(preview.total).toFixed(2)}</b>
          {preview.discount_total > 0 && <span style={{ marginLeft: 12, color: "#065f46" }}>(sconti € {Number(preview.discount_total).toFixed(2)})</span>}
        </div>
      )}
    </div>
  );
}

function ImportLineRow({ l, previewLine, importId, onReload, disabled }) {
  const { apiGet, apiPost } = useAuth();
  const [manual, setManual] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  // v43.35.20 — visible search state. Previous builds swallowed every
  // backend failure with `.catch(() => [])`, which meant the operator
  // saw an empty panel when the request 404'd, 403'd or threw. Now:
  //   searching=true  → "Ricerca…"
  //   searchErr set   → red "Errore ricerca prodotti: <msg>"
  //   searched && !results → "Nessun prodotto trovato"
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState("");
  const [searched, setSearched] = useState(false);
  const [picking, setPicking] = useState(false);
  const [pickErr, setPickErr] = useState("");
  // v43.35.11f — visual review state. If the backend flagged this line
  // with the EMAIL_IMPORT soft-review tokens the row shows "Da verificare"
  // (amber) instead of a definitive green MATCHED.
  const _ve = (l.validation_error || "");
  const _isCandidate = _ve.includes("email_import_sku_candidate_requires_verification");
  const _isMultirow = _ve.includes("email_import_description_multirow_suspected");
  const statusColor = _isCandidate
    ? "#92400e"
    : { MATCHED: "#065f46", NOT_FOUND: "#991b1b", AMBIGUOUS: "#92400e", INVALID: "#991b1b" }[l.match_status] || "#374151";
  const displayStatus = _isCandidate ? "Da verificare" : l.match_status;
  // Same-basis price display (v43.35.11f)
  const _ge_kg = previewLine?.ge_canonical_unit_price;
  const _ge_collo = previewLine?.ge_price_per_carton;
  const _delta_collo = previewLine?.delta_per_carton_eur;

  const search = async () => {
    if (!query || query.length < 2) {
      setSearchErr("Digita almeno 2 caratteri");
      setResults([]);
      setSearched(false);
      return;
    }
    setSearching(true);
    setSearchErr("");
    setSearched(false);
    try {
      // v43.35.20 — real root cause of the previously-reported "LOGISTICS
      // non riesce ad associare": the frontend was calling
      // `/api/admin/entities/products` (404 Not Found) while the backend
      // mounts the admin products catalogue at `/api/admin/products`
      // (routes_admin_entities.py — router prefix `/admin`, path
      // `/products`). The `.catch(() => [])` wrapper hid the 404, leaving
      // the UI silently empty for every operator — LOGISTICS and ADMIN
      // alike. Fixed URL + no silent swallow; errors surface visibly.
      const r = await apiGet(`/api/admin/products?q=${encodeURIComponent(query)}&status=ACTIVE&limit=20`);
      const items = Array.isArray(r) ? r : (r?.items || []);
      // Exact SKU first, then prefix, then partial.
      const qUp = (query || "").toUpperCase();
      items.sort((a, b) => {
        const aExact = (a.sku || "").toUpperCase() === qUp ? 0 : 1;
        const bExact = (b.sku || "").toUpperCase() === qUp ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;
        const aPref = (a.sku || "").toUpperCase().startsWith(qUp) ? 0 : 1;
        const bPref = (b.sku || "").toUpperCase().startsWith(qUp) ? 0 : 1;
        return aPref - bPref;
      });
      setResults(items);
    } catch (e) {
      setResults([]);
      const detail = e?.data?.detail;
      const msg = (typeof detail === "string" && detail)
        || (detail && typeof detail === "object" && (detail.code || JSON.stringify(detail)))
        || e?.message
        || "errore sconosciuto";
      const status = e?.status ? ` (HTTP ${e.status})` : "";
      setSearchErr(`Errore ricerca prodotti${status}: ${msg}`);
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };
  const pick = async (pid) => {
    setPicking(true);
    setPickErr("");
    try {
      await apiPost(`/api/admin/order-imports/${importId}/lines/${l.id}/match`, { product_id: pid });
      setManual(false);
      onReload();
    } catch (e) {
      const detail = e?.data?.detail;
      const msg = (typeof detail === "string" && detail)
        || (detail && typeof detail === "object" && (detail.code || JSON.stringify(detail)))
        || e?.message
        || "errore sconosciuto";
      const status = e?.status ? ` (HTTP ${e.status})` : "";
      setPickErr(`Associazione fallita${status}: ${msg}`);
    } finally {
      setPicking(false);
    }
  };
  const removeLine = async () => {
    if (!window.confirm("Rimuovere questa riga?")) return;
    await apiDelete(`/api/admin/order-imports/${importId}/lines/${l.id}`);
    onReload();
  };

  return (
    <>
      <tr data-testid={`line-${l.id}`} style={{ borderTop: "1px solid #eee" }}>
        <td style={{ padding: "6px" }}>{l.row_number}</td>
        <td style={{ padding: "6px", fontFamily: "monospace" }}>{l.external_sku || "—"}</td>
        <td style={{ padding: "6px" }}>{l.external_description || "—"}</td>
        <td style={{ padding: "6px" }}>{l.external_quantity ?? "—"}</td>
        <td style={{ padding: "6px" }}>{l.external_uom || "—"}</td>
        <td style={{ padding: "6px", fontFamily: "monospace" }}>{l.matched_product?.sku || "—"}</td>
        <td style={{ padding: "6px" }}>{l.matched_product?.name || "—"}</td>
        <td style={{ padding: "6px" }}>{l.normalized_cartons ?? "—"}</td>
        <td style={{ padding: "6px" }}>{l.normalized_kg ?? "—"}</td>
        <td style={{ padding: "6px" }}>{l.external_unit_price != null ? `€ ${Number(l.external_unit_price).toFixed(2)}` : "—"}</td>
        <td style={{ padding: "6px" }}>{_ge_kg != null ? `€ ${Number(_ge_kg).toFixed(3)}` : "—"}</td>
        <td style={{ padding: "6px" }}>{_ge_collo != null ? `€ ${Number(_ge_collo).toFixed(2)}` : "—"}</td>
        <td style={{ padding: "6px", color: _delta_collo == null ? "#666" : (_delta_collo >= 0 ? "#065f46" : "#991b1b") }}>{_delta_collo != null ? `€ ${Number(_delta_collo).toFixed(2)}` : "—"}</td>
        <td style={{ padding: "6px", color: statusColor, fontWeight: 600 }} data-testid={`match-state-${l.id}`}>{displayStatus}</td>
        <td style={{ padding: "6px", fontSize: 11, color: "#991b1b" }}>{_isCandidate || _isMultirow ? [_isCandidate && "SKU da verificare", _isMultirow && "descrizione multi-riga"].filter(Boolean).join(" · ") : (l.validation_error || "")}</td>
        <td style={{ padding: "6px", whiteSpace: "nowrap" }}>
          {!disabled && (
            <>
              {/* v43.35.17 — authorized reviewers must be able to OVERRIDE an
                   auto-match too (not just fill NOT_FOUND/AMBIGUOUS). The
                   backend guard is role-based (`_order_import_reviewer`), not
                   status-based: `manual_match_line` accepts any status except
                   CONFIRMED/REJECTED (enforced via parent import status
                   downstream). Label flips to "CAMBIA" when there's already
                   a GE match. */}
              <button data-testid={`match-btn-${l.id}`}
                      onClick={() => setManual(!manual)}
                      style={{ fontSize: 11, marginRight: 4 }}>
                {l.match_status === "MATCHED" ? "CAMBIA" : "ASSOCIA"}
              </button>
              <button data-testid={`delete-btn-${l.id}`} onClick={removeLine} style={{ fontSize: 11, color: "#991b1b" }}>✕</button>
            </>
          )}
        </td>
      </tr>
      {manual && (
        <tr style={{ background: "#f9fafb" }}>
          <td colSpan={15} style={{ padding: 12 }}>
            {/* v43.35.19 — ENTER triggers the same searchProducts() as the
                "Cerca" button. Using a <form> + onSubmit lets the browser
                fire the handler on ENTER naturally while preventDefault()
                blocks any accidental HTML submit / page reload. The modal
                row stays open; results render below as before. */}
            <form data-testid={`search-form-${l.id}`}
                  onSubmit={(e) => { e.preventDefault(); search(); }}
                  style={{ display: "flex", gap: 8 }}>
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca SKU o nome" style={{ flex: 1, padding: 6 }} data-testid={`search-${l.id}`} disabled={searching} />
              <button type="submit" data-testid={`search-btn-${l.id}`} disabled={searching}>{searching ? "Ricerca…" : "Cerca"}</button>
            </form>
            {searchErr && (
              <div data-testid={`search-err-${l.id}`}
                   style={{ marginTop: 8, padding: 8, background: "#fee2e2", color: "#991b1b", borderRadius: 4, fontSize: 12 }}>
                {searchErr}
              </div>
            )}
            {!searchErr && searched && results.length === 0 && !searching && (
              <div data-testid={`search-empty-${l.id}`}
                   style={{ marginTop: 8, padding: 8, background: "#f3f4f6", color: "#374151", borderRadius: 4, fontSize: 12 }}>
                Nessun prodotto trovato
              </div>
            )}
            {pickErr && (
              <div data-testid={`pick-err-${l.id}`}
                   style={{ marginTop: 8, padding: 8, background: "#fee2e2", color: "#991b1b", borderRadius: 4, fontSize: 12 }}>
                {pickErr}
              </div>
            )}
            {results.length > 0 && (
              <ul style={{ margin: 8, padding: 0, listStyle: "none", maxHeight: 200, overflow: "auto" }}>
                {results.map(p => (
                  <li key={p.id} style={{ padding: 6, borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between" }}>
                    <span><code>{p.sku}</code> · {p.name}</span>
                    <button onClick={() => pick(p.id)} data-testid={`pick-${p.id}`} disabled={picking}>{picking ? "…" : "SELEZIONA"}</button>
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default AdminOrderImportsListPage;
